import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Modal } from '../components/ui';
import { LineCard, MultiLineCard, type Point } from '../components/Chart';
import { ImplementationCode, useBrand } from '../components/ImplementationCode';
import { AiInsightsModal, type AiResult } from '../components/AiInsightsModal';
import { AudienceContext, type AudienceContextInsights } from '../components/AudienceContext';
import { api, ApiError } from '../lib/api';
import { currentUser } from '../lib/auth';
import type { ApiResponse } from '../lib/types';
import { calendarShort, calendarSortKey, calendarDate } from '../lib/calendarDate';
import { ABORT_LEGEND } from '../lib/abortLegend';
import { BudgetChoice, PulseModel, useCampaignBudget, usd, clickBudget, type BudgetAction } from '../components/CampaignBudget';
import { CampaignDeliveryAlert, type CampaignDeliveryHealth } from '../components/CampaignDeliveryAlert';
import { DailyClicksHistory, type HistoryRow } from '../components/DailyClicksHistory';
import {CampaignShareAlert,type ShareStatus} from '../components/CampaignShare';

interface DayReport { clicks?: number; attempt?: number; pageviews?: number; branding_success?: number; branding_engaged?: number; branding_bounced?: number; branding_failed?: number }
interface ReportData { today: DayReport | null; history?: HistoryRow[]; event_types?: string[]; campaign?: Record<string, unknown> }
interface Campaign {
  share_status?: ShareStatus|null;
  catalog_delivery?: CampaignDeliveryHealth | null;
  catalog_recommendation_id?: string | null;
  id: string; name: string; campaign_type?: string; status?: string; method?: string; platform?: string; clicks?: number;
  max_attempts_per_minute?: number; max_attempt_percent?: number;
  campaign_id?: string; campaign_js_id?: string; client_label?: string;
  primary_domain?: string; target_url?: string; site_type?: string; total_sessions?: number;
  session_time_min?: number; session_time_max?: number; session_time_p50?: number; bounce_rate?: number;
  isolation_reset_profile?: boolean; isolation_clear_cookies?: boolean; isolation_incognito?: boolean;
  firstclick_allow?: string | string[] | null; firstclick_block?: string | string[] | null;
}
interface Metrics {
  clicks?: { datapoints: { timestamp: string; value: number }[]; total: number };
  attempts?: { datapoints: { timestamp: string; value: number }[]; total: number };
  aborts?: { total: number };
  abortDetails?: Record<string, number>;
  abortTimeseries?: Record<string, { timestamp: string; value: number }[]>;
}
interface ChangeEntry { old?: unknown; new?: unknown }
interface CampaignLog {
  id: number;
  action: string;
  user_email?: string | null;
  changes?: Record<string, ChangeEntry> | string | null;
  created_at: string;
}

// Mesmos rótulos do front antigo, para o log não mudar de vocabulário.
const LOG_FIELD_LABELS: Record<string, string> = {
  max_share_pct: 'Max Share %', share_rules: 'Share conditions', share_rule: 'Share rule',
  name: 'Name', status: 'Status', period_start: 'Start Date', period_finish: 'End Date',
  method: 'Method', platform: 'Platform', clicktracker: 'Click Tracker', clicks: 'Clicks/Day',
  max_attempt_percent: 'Max Attempt %', max_attempts_per_minute: 'Attempts/Minute',
  frequency: 'Frequency', frequency_hour: 'Frequency Hours',
  pageviews_min: 'Min Pageviews', pageviews_max: 'Max Pageviews',
  device: 'Device', time_range: 'Time Range', min_user_days: 'Min User Days',
  destination_set_id: 'Destination Set', client_id: 'Client ID',
  campaign_client_id: 'Campaign Client ID', campaign_type: 'Campaign Type',
  custom_cpc: 'Custom CPC', referrer_check: 'Referrer Check',
  fraud_guard_enabled: 'Fraud Guard', event_types: 'Event Types',
};
const logFieldLabel = (f: string) =>
  LOG_FIELD_LABELS[f] || f.replace(/_/g, ' ').replace(/\w/g, (c) => c.toUpperCase());

const LOG_ACTION_TONE: Record<string, 'green' | 'blue' | 'red' | 'amber'> = {
  create: 'green', update: 'blue', delete: 'red', duplicate: 'amber', status_change: 'blue',
};

/** Valor de um campo no log. Datas usam o helper de calendário: passar por
 *  Date e renderizar em BRT mostraria o dia anterior. */
function logValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return 'empty';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return calendarDate(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface AlbBlockRule { domain: string; priority: number; ruleArn: string }

interface InsightsRetention {
  users: number;
  returning_users: number;
  returning_pct: number | null;
  avg_days_returning: number | null;
  avg_gap_days: number | null;
  byDaysSeen: { days_seen: number; users: number }[];
  gapBuckets: { bucket: string; users: number }[];
}
interface InsightsDetail {
  schema_version?: number;
  computed_at?: string;
  context?: AudienceContextInsights;
  totals?: { hits?: number; unique_users?: number; unique_ips?: number };
  byDay?: { day: string; hits: number; unique_users: number }[];
  byEvent?: { event: string; hits: number; unique_users: number }[];
  byDevice?: { device: string; hits: number; unique_users: number }[];
  bySource?: { category: string; hits: number; unique_users: number }[];
  byUtm?: { source: string; medium: string; campaign: string; hits: number; unique_users: number }[];
  byReferrer?: { host: string; hits: number; unique_users: number }[];
  byUtmRollup?: {
    source: { key: string; hits: number; unique_users: number }[];
    medium: { key: string; hits: number; unique_users: number }[];
    campaign: { key: string; hits: number; unique_users: number }[];
  };
  retention?: InsightsRetention | null;
}
interface Insights {
  unique_users?: number | null;
  hits?: number | null;
  data?: InsightsDetail | null;
  updated_at?: string;
}

// Rótulos e cores das categorias de origem (mesma taxonomia do First Click stamp).
const SOURCE_META: Record<string, { label: string; color: string }> = {
  google_ads: { label: 'Google Ads', color: '#4285F4' },
  meta_ads: { label: 'Meta Ads', color: '#0866FF' },
  paid: { label: 'Other paid', color: '#f59e0b' },
  organic_search: { label: 'Organic search', color: '#22c55e' },
  social: { label: 'Social', color: '#a855f7' },
  referral: { label: 'Referral', color: '#14b8a6' },
  direct: { label: 'Direct', color: '#64748b' },
  organic: { label: 'Organic', color: '#94a3b8' },
};

interface ProductCheck {
  hasDestination: boolean;
  destination?: { id: string; name?: string | null };
  eventsScanned?: number;
  eventsWithProduct?: number;
  uniqueIds?: number;
  matched?: { id: string; name?: string | null; inStock?: boolean | null }[];
  unmatched?: string[];
}

interface FraudLatest {
  latest: { verdict: string; summary: string; signals?: string[]; mode: string; window: string; created_at: string; model?: string | null; tokens?: number | null } | null;
  rules: Array<{ type: string; value: string; campaign_id: string; hits?: number; last_hit_at?: string; still_filtering?: boolean }>;
}

// Os valores precisam bater com o switch do getReport (app/model/campaigns.js):
// 'last7' etc. não casavam e caíam no default = mês atual.
const DATE_FILTERS = [
  { v: 'today', l: 'Today' }, { v: 'yesterday', l: 'Yesterday' }, { v: 'last7days', l: 'Last 7 days' },
  { v: 'currentmonth', l: 'Current Month' }, { v: 'last30days', l: 'Last 30 days' },
  { v: 'last60days', l: 'Last 60 days' }, { v: 'last90days', l: 'Last 90 days' },
];
const RT_PERIODS = ['1h', '3h', '13h', '1d', '3d', '7d'];
const n = (v: unknown) => Number(v) || 0;

// campaigns_report.date é coluna DATE: data de calendário, não instante.
// Passar por new Date() e ler getDate() em BRT mostra o dia ANTERIOR.
function dayLabel(r: HistoryRow): string {
  return calendarShort(r.date || r.id_yyyymmdd || '');
}
/** Chave numérica p/ ordenar as linhas do histórico por data. */
/** Chave numérica p/ ordenar o histórico. YYYYMMDD, sem construir Date. */
function dateKey(r: HistoryRow): number {
  return calendarSortKey(r.date || r.id_yyyymmdd || '');
}
function hm(ts: string): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** Timestamp em horário local (BRT), não UTC cru. */
function fmtTs(v: unknown): string {
  if (v == null || v === '') return '—';
  const d = new Date(typeof v === 'number' ? v : String(v));
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function ExpandableCode({ text, previewLen = 55 }: { text: string; previewLen?: number }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-slate-300">—</span>;
  const preview = text.slice(0, previewLen) + (text.length > previewLen ? '…' : '');
  return (
    <div>
      <code onClick={() => setOpen((o) => !o)} className="cursor-pointer text-rose-600 font-mono text-[11px] hover:underline" title="Click to expand/collapse">
        {open ? '▾ ' : '▸ '}{preview}
      </code>
      {open && (
        <pre className="mt-1.5 max-h-64 overflow-auto rounded-md bg-slate-900 text-slate-100 p-3 text-[11px] leading-relaxed whitespace-pre-wrap break-all">{text}</pre>
      )}
    </div>
  );
}

function Tile({ value, label, sub, tone, children }: { value: string; label: string; sub?: string; tone: string; children?: React.ReactNode }) {
  return (
    <div className={`relative rounded-xl p-5 text-white ${tone}`}>
      {children}
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-1">{label}</div>
      {sub && <div className="text-xs opacity-90">{sub}</div>}
    </div>
  );
}

export default function CampaignView() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Cliente (não-admin) vê um relatório enxuto: cliques, pageviews e o script de
  // implementação. Métricas CloudWatch, Fraud Guard, tag events, debug e change
  // log são admin-only (os endpoints também são/ficam restritos no backend).
  const isAdmin = !!currentUser()?.isAdmin;
  const [campaignBrandLabel, setCampaignBrandLabel] = useState<string | null>(null);
  const campaignBrand = useBrand(campaignBrandLabel);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [deliveryHealthError, setDeliveryHealthError] = useState(false);
  const [report, setReport] = useState<ReportData | null>(null);
  const [dateFilter, setDateFilter] = useState('currentmonth');
  const [loadingReport, setLoadingReport] = useState(true);
  const [reportUpdatedAt, setReportUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState(0);
  const [targetBusy, setTargetBusy] = useState(false);
  const budget = useCampaignBudget(isAdmin ? campaign?.catalog_recommendation_id || undefined : undefined);
  const [pendingTarget, setPendingTarget] = useState<number|null>(null);
  useEffect(() => { if (budget.data) setTarget(budget.data.target_clicks); }, [budget.data]);

  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [rtPeriod, setRtPeriod] = useState('1h');
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [aiTitle, setAiTitle] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInsBusy, setAiInsBusy] = useState(false);

  const [tagEvents, setTagEvents] = useState<Record<string, unknown>[]>([]);
  const [tagFilter, setTagFilter] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [prodCheck, setProdCheck] = useState<ProductCheck | null>(null);
  const [prodCheckOpen, setProdCheckOpen] = useState(false);
  const [prodCheckBusy, setProdCheckBusy] = useState(false);
  const [albBlocked, setAlbBlocked] = useState<AlbBlockRule[]>([]);
  const [albBusy, setAlbBusy] = useState(false);

  // Audience Insights (Athena/ALB logs) — cache no Postgres, refresh sob demanda
  const nowD = new Date();
  const [insMonth, setInsMonth] = useState(nowD.getMonth() + 1);
  const [insYear, setInsYear] = useState(nowD.getFullYear());
  const [insights, setInsights] = useState<Insights | null>(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const [utmGroupBy, setUtmGroupBy] = useState<'source' | 'medium' | 'campaign'>('source');
  const [debugJs, setDebugJs] = useState<Record<string, unknown>[]>([]);
  const [debugLoaded, setDebugLoaded] = useState(false);
  const [debugBusy, setDebugBusy] = useState(false);
  const [fraud, setFraud] = useState<FraudLatest | null>(null);
  const [logs, setLogs] = useState<CampaignLog[]>([]);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [fraudBusy, setFraudBusy] = useState<string | null>(null);

  // campanha (header + target)
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setCampaign(null);
    setDeliveryHealthError(false);
    const refresh = (initial: boolean) => api.get<ApiResponse<Campaign>>(`/campaigns/get?id=${id}`).then((r) => {
      if (!alive) return;
      setCampaign(r.data || null);
      setDeliveryHealthError(false);
      setTarget(n(r.data?.clicks));
      setCampaignBrandLabel(r.data?.client_label || null);
    }).catch((e) => {
      if (!alive) return;
      if (initial) setError(e instanceof ApiError ? e.message : 'Failed to load');
      else setDeliveryHealthError(true);
    });
    refresh(true);
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(false); }, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, [id]);

  // report (tiles + history + daily charts) — recarrega sozinho.
  //
  // Cadência de 60s porque é o intervalo do syncreport: buscar mais rápido leria
  // o mesmo número do Postgres e só gastaria query à toa.
  useEffect(() => {
    if (!id) return;
    let alive = true;

    const fetchReport = (showLoading: boolean) => {
      if (showLoading) setLoadingReport(true);
      api.get<ApiResponse<ReportData>>(`/campaigns/report?id=${id}&dateFilter=${dateFilter}`)
        .then((r) => {
          if (!alive) return;
          setReport(r.data || null);
          setReportUpdatedAt(new Date());
          setError(null);
        })
        .catch((e) => {
          if (!alive) return;
          // Falha em atualização de fundo não apaga o dado que já está na tela.
          if (showLoading) setError(e instanceof ApiError ? e.message : 'Failed to load');
        })
        .finally(() => { if (alive && showLoading) setLoadingReport(false); });
    };

    fetchReport(true);
    const t = setInterval(() => {
      // Aba em segundo plano não precisa de dado fresco — evita bater no
      // Postgres e no DynamoDB com a tela que ninguém está olhando.
      if (document.visibilityState === 'visible') fetchReport(false);
    }, 60000);

    return () => { alive = false; clearInterval(t); };
  }, [id, dateFilter]);

  // métricas CloudWatch — surface o erro em vez de mostrar 0; auto-refresh a cada 30s
  useEffect(() => {
    if (!id || !isAdmin) return;
    let alive = true;
    const fetchMetrics = (showLoading: boolean) => {
      if (showLoading) setLoadingMetrics(true);
      api.get<ApiResponse<Metrics>>(`/campaigns/metrics?id=${id}&period=${rtPeriod}`)
        .then((r) => { if (!alive) return; setMetrics(r.data || null); setMetricsError(null); })
        .catch((e) => { if (!alive) return; if (showLoading) setMetrics(null); setMetricsError(e instanceof ApiError ? e.message : 'Failed to load metrics'); })
        .finally(() => { if (alive && showLoading) setLoadingMetrics(false); });
    };
    fetchMetrics(true);
    const t = setInterval(() => fetchMetrics(false), 30000); // real-time
    return () => { alive = false; clearInterval(t); };
  }, [id, rtPeriod, isAdmin]);

  useEffect(() => {
    if (!id) return;
    loadTagEvents(); // cliente também vê os tag events (posse validada no backend)
    if (!isAdmin) return;
    loadFraud();
  }, [id, isAdmin]);

  useEffect(() => {
    if(!id||!isAdmin)return;
    api.get<ApiResponse<CampaignLog[]>>(`/campaigns/logs?id=${id}`)
      .then((r) => { setLogs(r.data || []); setLogsError(null); })
      .catch((e) => setLogsError(e instanceof ApiError ? e.message : 'Failed to load change log'));
    // Debug GetJS NAO carrega sozinho: e uma Query no DynamoDB e quase nunca e
    // o motivo de abrir a tela. Sob demanda, como no front antigo.
  }, [id, isAdmin, campaign?.clicks]);

  async function loadDebugJs() {
    if (!id) return;
    setDebugBusy(true);
    try {
      const r = await api.get<ApiResponse<Record<string, unknown>[]>>(`/campaigns/debug-getjs?id=${id}`);
      setDebugJs(r.data || []);
      setDebugLoaded(true);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Failed to load getJS debug data');
    } finally {
      setDebugBusy(false);
    }
  }

  function loadTagEvents() {
    if (!id) return;
    setTagBusy(true);
    api.get<ApiResponse<Record<string, unknown>[]>>(`/campaigns/tag-events?id=${id}`)
      .then((r) => setTagEvents(r.data || []))
      .catch(() => {})
      .finally(() => setTagBusy(false));
  }
  async function clearAndRecollect() {
    if (!id) return;
    if (!confirm('Clear the collected events and start collecting again?')) return;
    setTagBusy(true);
    try { await api.del(`/campaigns/tag-events/clear?id=${id}`); } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); }
    loadTagEvents();
  }

  // --- Bloqueio no ALB da Audience (admin): regra por domínio do Referer ---
  function loadAlbBlocked() {
    api.get<ApiResponse<AlbBlockRule[]>>('/campaigns/alb-block')
      .then((r) => setAlbBlocked(r.data || []))
      .catch(() => {});
  }
  useEffect(() => { if (isAdmin) loadAlbBlocked(); }, [isAdmin]);

  // Domínio da campanha inferido pelo referrer mais frequente dos tag events.
  const campaignDomain = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of tagEvents) {
      const r = String(e.referrer || e.referer || '');
      if (!r) continue;
      try {
        const h = new URL(r).hostname.replace(/^www\./, '');
        counts.set(h, (counts.get(h) || 0) + 1);
      } catch { /* referrer inválido — ignora */ }
    }
    let best = ''; let n = 0;
    counts.forEach((c, h) => { if (c > n) { n = c; best = h; } });
    return best;
  }, [tagEvents]);
  const domainBlocked = !!campaignDomain && albBlocked.some((b) => b.domain === campaignDomain);

  async function toggleAlbBlock() {
    const typed = window.prompt(
      domainBlocked
        ? 'Unblock this domain at the Audience ALB:'
        : 'Block this domain at the Audience ALB (matches the Referer header — traffic dies at the load balancer):',
      campaignDomain
    );
    if (!typed || !typed.trim()) return;
    const norm = typed.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
    const isBlocked = albBlocked.some((b) => b.domain === norm);
    setAlbBusy(true);
    try {
      const r = await api.post<ApiResponse<unknown>>('/campaigns/alb-block', { domain: norm, action: isBlocked ? 'unblock' : 'block' });
      if (!r.success) throw new Error(r.error || r.message || 'Failed');
      loadAlbBlocked();
      alert(isBlocked ? `Unblocked ${norm} — traffic reaches the pixel again.` : `Blocked ${norm} — the ALB now answers 403 for this Referer.`);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed');
    } finally {
      setAlbBusy(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    setInsights(null);
    api.get<ApiResponse<Insights | null>>(`/campaigns/insights?id=${id}&month=${insMonth}&year=${insYear}`)
      .then((r) => setInsights(r.data || null))
      .catch(() => {});
  }, [id, insMonth, insYear]);

  async function refreshInsights() {
    if (!id) return;
    setInsightsBusy(true);
    try {
      const r = await api.post<ApiResponse<Insights>>('/campaigns/insights/refresh', { id, month: insMonth, year: insYear });
      if (!r.success) throw new Error(r.error || r.message || 'Failed');
      setInsights(r.data || null);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to refresh insights');
    } finally {
      setInsightsBusy(false);
    }
  }

  // IA analisa os gráficos de entrega do período selecionado (1h…7d).
  // Modelo: o configurado em Settings (default Claude Opus 5). Admin-only.
  async function analyzeDelivery() {
    if (!id || !metrics) return;
    setAiBusy(true);
    try {
      const r = await api.post<ApiResponse<AiResult>>('/campaigns/analyze-delivery', {
        campaignId: id,
        period: rtPeriod,
        metrics,
        todayReport: { clicks_today: clicksToday, daily_target: target },
      });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      setAiTitle(`✨ Delivery Insights (${rtPeriod})`);
      setAiResult(r.data);
      setAiOpen(true);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to analyze');
    } finally {
      setAiBusy(false);
    }
  }

  // IA analisa os Audience Insights do mês selecionado (usa o cache do
  // Postgres — precisa do Refresh do card antes). Admin-only.
  async function analyzeInsightsAI() {
    if (!id) return;
    setAiInsBusy(true);
    try {
      const r = await api.post<ApiResponse<AiResult>>('/campaigns/analyze-insights', {
        campaignId: id,
        month: insMonth,
        year: insYear,
      });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      setAiTitle(`✨ Audience Insights (${insMonth}/${insYear})`);
      setAiResult(r.data);
      setAiOpen(true);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to analyze');
    } finally {
      setAiInsBusy(false);
    }
  }

  async function checkProducts() {
    if (!id) return;
    setProdCheckBusy(true);
    try {
      const r = await api.get<ApiResponse<ProductCheck>>(`/campaigns/check-tag-products?id=${id}`);
      setProdCheck(r.data || null);
      setProdCheckOpen(true);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Failed to check product IDs');
    } finally {
      setProdCheckBusy(false);
    }
  }

  function loadFraud() {
    if (!id) return;
    api.get<ApiResponse<unknown> & FraudLatest>(`/fraud-guard/latest?campaignId=${id}`)
      .then((r: any) => setFraud({ latest: r.latest ?? null, rules: r.rules ?? [] }))
      .catch(() => {});
  }

  // --- Target +/- (persiste + recalcula pace) ---
  const clicksToday = Math.max(n(report?.today?.clicks), budget.data?.consumption?.clicks || 0);
  const now = new Date();
  const hoursElapsed = now.getHours() + now.getMinutes() / 60;
  const hoursRemaining = 24 - hoursElapsed;
  // Com a meta já batida o restante é negativo, e Math.ceil(-0.5) devolve -0,
  // que o toLocaleString imprime como "-0". Não existe ritmo negativo: o que
  // falta entregar é zero.
  const clicksPerHour = hoursRemaining > 0 ? Math.max(0, Math.ceil((target - clicksToday) / hoursRemaining)) : 0;
  const expected = hoursElapsed > 0 ? Math.floor((target / 24) * hoursElapsed) : 0;
  const pace = expected > 0 ? Math.round((clicksToday / expected) * 100) : 0;
  const onPace = clicksToday >= expected;
  const shareControl = campaign?.share_status?.control;
  // Only pair a rule with the target it actually produced, including after manual edits.
  const targetControl = shareControl && ['increase', 'reduce', 'pause'].includes(shareControl.action)
    && Number.isFinite(shareControl.base_clicks) && shareControl.base_clicks >= 0
    && shareControl.target_clicks === target ? shareControl : null;
  const targetLimitedByBudget = targetControl?.action === 'increase'
    && target < Math.floor(targetControl.base_clicks * (1 + targetControl.adjustment_pct / 100));

  async function adjustTarget(step: number) {
    if (!campaign || targetBusy || budget.busy) return;
    const next = Math.max(0, target + step);
    if (next === target) return;
    if (campaign?.catalog_recommendation_id) {
      if (isAdmin && budget.data && next > 0) setPendingTarget(next);
      return;
    }
    setTargetBusy(true);
    setTarget(next);
    try {
      const r = await api.post<ApiResponse<{ clicks: number }>>('/campaigns/update-clicks', { id, clicks: next });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed to update target');
      setTarget(r.data.clicks);
    } catch (e) {
      setTarget(target);
      alert(e instanceof Error ? e.message : 'Failed to update target');
    } finally {
      setTargetBusy(false);
    }
  }
  async function confirmTarget(action: BudgetAction) {
    if (pendingTarget == null) return;
    try { await budget.save({target_clicks:pendingTarget,budget_action:action}); setPendingTarget(null); } catch { /* error stays in the dialog */ }
  }

  async function runFraud(path: string, body: Record<string, unknown>, key: string) {
    setFraudBusy(key);
    try { await api.post(path, body); loadFraud(); } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); } finally { setFraudBusy(null); }
  }
  async function duplicate() {
    try { await api.post(`/campaigns/duplicate?id=${id}`, {}); navigate('/campaigns'); } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); }
  }
  async function del() {
    if (!confirm('Delete this campaign?')) return;
    try { await api.del(`/campaigns/delete?id=${id}`); navigate('/campaigns'); } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed'); }
  }

  const isBranding = campaign?.campaign_type === 'branding';
  const histAsc = [...(report?.history || [])].sort((a, b) => dateKey(a) - dateKey(b)); // cronológico p/ gráficos
  const histDesc = [...(report?.history || [])].sort((a, b) => dateKey(b) - dateKey(a)); // mais recente primeiro p/ tabela
  const mk = (sel: (r: HistoryRow) => number): Point[] => histAsc.map((r) => ({ x: dayLabel(r), y: sel(r) }));
  const rtClicks: Point[] = (metrics?.clicks?.datapoints || []).map((d) => ({ x: hm(d.timestamp), y: d.value }));
  const rtAttempts: Point[] = (metrics?.attempts?.datapoints || []).map((d) => ({ x: hm(d.timestamp), y: d.value }));
  const abortRows = Object.entries(metrics?.abortDetails || {}).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);
  const abortTotal = abortRows.reduce((a, [, c]) => a + c, 0);

  // Abort Reasons Over Time: uma linha por motivo (só os que tiveram volume).
  const ABORT_COLORS = ['var(--series-ink)', '#e74c3c', '#f39c12', '#3498db', '#27ae60', '#9b59b6', '#e67e22', '#16a085', '#c0392b', '#7f8c8d'];
  const abortSeries = (() => {
    const ts = metrics?.abortTimeseries || {};
    const active = Object.entries(ts)
      .map(([reason, pts]) => ({ reason, pts: pts || [], total: (pts || []).reduce((a, p) => a + n(p.value), 0) }))
      .filter((s) => s.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
    if (active.length === 0) return { rows: [] as Array<Record<string, string | number>>, series: [] as { key: string; name: string; color: string }[] };
    // União ordenada dos timestamps. Antes as linhas eram indexadas por HH:MM,
    // o que (a) colapsava dias diferentes em 1d/3d/7d e (b) deixava o eixo X fora
    // de ordem quando uma série trazia um horário que a anterior não tinha.
    const times = [...new Set(active.flatMap((s) => s.pts.map((p) => p.timestamp)))].sort();
    const spanMs = times.length > 1 ? new Date(times[times.length - 1]).getTime() - new Date(times[0]).getTime() : 0;
    const multiDay = spanMs > 24 * 3600 * 1000;
    const label = (iso: string) => {
      const d = new Date(iso);
      return multiDay ? `${d.getMonth() + 1}/${d.getDate()} ${hm(iso)}` : hm(iso);
    };
    const at = new Map(times.map((t, i) => [t, i]));
    const rows: Array<Record<string, string | number>> = times.map((t) => ({ x: label(t) }));
    active.forEach((s, i) => {
      // O CloudWatch só devolve datapoint onde houve volume; ausência = zero
      // aborts, não "sem dado" — preencher com 0 evita linha quebrada.
      for (const r of rows) r[`s${i}`] = 0;
      s.pts.forEach((p) => { rows[at.get(p.timestamp)!][`s${i}`] = n(p.value); });
    });
    return {
      rows,
      series: active.map((s, i) => ({ key: `s${i}`, name: s.reason, color: ABORT_COLORS[i % ABORT_COLORS.length] })),
    };
  })();

  return (
    <Layout
      title="Campaign Report"
      actions={<>{campaign?.catalog_recommendation_id && <Button onClick={() => navigate(`/campaigns/recommendations/${id}`)}>Recommendations</Button>}<Button variant="ghost" onClick={() => navigate('/campaigns')}>← Back to List</Button></>}
    >
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}
      {deliveryHealthError && <p role="status" className="mb-3 text-sm text-amber-700">Não foi possível atualizar a situação da entrega. Os dados abaixo podem estar desatualizados.</p>}
      <CampaignDeliveryAlert health={campaign?.catalog_delivery} />

      {/* Header */}
      <Card className="p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-ink-900">{campaign?.name || '—'}</h2>
              <Badge tone={(campaign?.campaign_type || 'retargeting') === 'branding' ? 'blue' : 'neutral'}>{campaign?.campaign_type || 'retargeting'}</Badge>
              <Badge tone={campaign?.share_status?.control?.action === 'pause' ? 'red' : campaign?.status === 'on' ? 'green' : 'neutral'}>{campaign?.status === 'on' && campaign?.share_status?.control?.action === 'pause' ? 'Paused by share rule' : campaign?.status || '—'}</Badge>
            </div>
            <div className="mt-2 text-sm text-slate-500 space-y-0.5">
              <div><span className="font-medium text-slate-600">ID:</span> <span className="font-mono">{id}</span></div>
              {isBranding ? (
                <>
                  <div><span className="font-medium text-slate-600">Primary Domain:</span> {campaign?.primary_domain || 'N/A'}</div>
                  <div><span className="font-medium text-slate-600">Target URL:</span> {campaign?.target_url || 'N/A'}</div>
                  <div><span className="font-medium text-slate-600">Site Type:</span> {campaign?.site_type || 'N/A'}</div>
                  <div><span className="font-medium text-slate-600">Total Sessions/Day:</span> {n(campaign?.total_sessions).toLocaleString()}</div>
                  <div><span className="font-medium text-slate-600">Session Time:</span> {n(campaign?.session_time_min)}s - {n(campaign?.session_time_max)}s (P50: {n(campaign?.session_time_p50)}s)</div>
                  <div><span className="font-medium text-slate-600">Bounce Rate:</span> {campaign?.bounce_rate != null ? `${(n(campaign.bounce_rate) * 100).toFixed(0)}%` : 'N/A'}</div>
                  <div className="flex gap-1.5 pt-1">
                    {campaign?.isolation_reset_profile && <Badge tone="green">Reset Profile</Badge>}
                    {campaign?.isolation_clear_cookies && <Badge tone="blue">Clear Cookies</Badge>}
                    {campaign?.isolation_incognito && <Badge tone="neutral">Incognito</Badge>}
                  </div>
                </>
              ) : isAdmin ? (
                <>
                  <div><span className="font-medium text-slate-600">Method:</span> {campaign?.method || '—'}</div>
                  <div><span className="font-medium text-slate-600">Platform:</span> {campaign?.platform || 'N/A'}</div>
                  <div><span className="font-medium text-slate-600">Total Clicks Target:</span> {target.toLocaleString()}</div>
                  <div><span className="font-medium text-slate-600">Attempts/Minute:</span> {campaign?.max_attempts_per_minute != null ? n(campaign.max_attempts_per_minute).toLocaleString() : '100'}</div>
                  <div><span className="font-medium text-slate-600">Max Attempt %:</span> {campaign?.max_attempt_percent != null ? `${n(campaign.max_attempt_percent)}%` : '0%'}</div>
                </>
              ) : (
                <div><span className="font-medium text-slate-600">Total Clicks Target:</span> {target.toLocaleString()}</div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/campaigns/edit/${id}`)}>✎ Edit</Button>
            {isAdmin && (
              <Button
                className={domainBlocked ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'}
                disabled={albBusy}
                onClick={toggleAlbBlock}
                title={campaignDomain ? `Domain detected from tag events: ${campaignDomain}` : 'Domain will be asked (no tag events loaded yet)'}
              >
                {albBusy ? '…' : domainBlocked ? '✓ Unblock traffic (ALB)' : '🚫 Block traffic (ALB)'}
              </Button>
            )}
            {isAdmin && <Button className="bg-amber-500 hover:bg-amber-600" onClick={duplicate}>⧉ Duplicate</Button>}
            {isAdmin && <Button variant="danger" onClick={del}>× Delete</Button>}
          </div>
        </div>
      </Card>

      {/* KPI tiles */}
      {budget.data&&<div className="mb-4"><PulseModel data={budget.data} /></div>}
      {budget.error && <p role="alert" className="text-sm text-rose-600 mb-3">{budget.error}</p>}
      {budget.data && pendingTarget != null && <BudgetChoice data={budget.data} amount={clickBudget(pendingTarget,budget.data.cpc_usd)} open busy={budget.busy} error={budget.error} onCancel={()=>setPendingTarget(null)} onChoose={confirmTarget} />}
      {isBranding ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Tile value={clicksToday.toLocaleString()} label="Sessions Today" sub="Total sessions" tone="bg-rose-500" />
          <Tile value={n(report?.today?.branding_success).toLocaleString()} label="Success" sub="Successful sessions" tone="bg-emerald-600" />
          <Tile value={n(report?.today?.branding_engaged).toLocaleString()} label="Engaged" sub="Engaged sessions" tone="bg-sky-500" />
          <Tile value={(n(report?.today?.branding_bounced) + n(report?.today?.branding_failed)).toLocaleString()} label="Bounced / Failed" sub={`${n(report?.today?.branding_bounced)} bounced / ${n(report?.today?.branding_failed)} failed`} tone="bg-amber-500" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Tile value={clicksToday.toLocaleString()} label="Clicks Today" sub="Delivered so far" tone="bg-rose-500" />
          <div aria-label="Click target today" className="relative min-w-0 rounded-xl bg-emerald-600 p-5 text-white">
            <div className="mb-3 min-h-8 pr-16 text-sm font-medium">Target Today</div>
            {(isAdmin || !campaign?.catalog_recommendation_id) && (
              <div className="absolute top-3 right-3 flex gap-1.5">
                <button aria-label="Decrease click target" disabled={!campaign || targetBusy || budget.busy || target === 0 || (!!campaign.catalog_recommendation_id && (!budget.data || target<=50))} onClick={() => adjustTarget(-50)} className="w-8 h-8 rounded-md bg-white/25 hover:bg-white/40 disabled:opacity-40 text-lg font-bold leading-none">−</button>
                <button aria-label="Increase click target" disabled={!campaign || targetBusy || budget.busy || (!!campaign.catalog_recommendation_id && !budget.data)} onClick={() => adjustTarget(50)} className="w-8 h-8 rounded-md bg-white/25 hover:bg-white/40 disabled:opacity-40 text-lg font-bold leading-none">+</button>
              </div>
            )}
            {targetControl ? <>
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2">
                <div>
                  <div className="text-xl lg:text-3xl font-bold [overflow-wrap:anywhere]">{targetControl.base_clicks.toLocaleString()}</div>
                  <div className="mt-1 text-xs opacity-90">Configured clicks</div>
                </div>
                <span aria-hidden="true" className="text-xl lg:text-3xl opacity-70">→</span>
                <div>
                  <div className="text-xl lg:text-3xl font-bold [overflow-wrap:anywhere]">{target.toLocaleString()}</div>
                  <div className="mt-1 text-xs opacity-90">{targetControl.action === 'pause' ? 'Paused by rule' : `Rule ${targetControl.action === 'reduce' ? '−' : '+'}${targetControl.adjustment_pct.toLocaleString()}%`}</div>
                </div>
              </div>
              <div className="mt-2 text-xs opacity-90">{targetLimitedByBudget ? 'Limited by budget' : 'Total clicks to deliver'}</div>
            </> : <>
              <div className="text-3xl font-bold [overflow-wrap:anywhere]">{target.toLocaleString()}</div>
              <div className="text-xs opacity-90">Total clicks to deliver</div>
            </>}
          </div>
          <Tile
            value={clicksPerHour.toLocaleString()}
            label="Clicks/Hour"
            sub={clicksToday >= target && target > 0 ? 'Target reached' : 'Required pace'}
            tone="bg-sky-500"
          />
          <Tile value={`${pace}%`} label="Pace" sub={onPace ? 'On Track' : 'Behind'} tone={onPace ? 'bg-emerald-600' : 'bg-rose-500'} />
          {budget.data && <>
            <Tile value={usd(budget.data.daily_budget_usd)} label="Daily Budget" sub={`CPC ${usd(budget.data.cpc_usd)} · ${budget.data.cpc_source === 'client' ? 'Client default' : 'Campaign'}`} tone="bg-indigo-600" />
            <Tile value={budget.data.consumption ? usd(budget.data.consumption.spent_usd) : 'Unavailable'} label="Spent Today" sub="USD · America/Sao_Paulo" tone="bg-violet-600" />
          </>}
          <CampaignShareAlert status={campaign?.share_status} compact />
        </div>
      )}

      {/* Date filter */}
      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          {DATE_FILTERS.map((f) => (
            <button key={f.v} onClick={() => setDateFilter(f.v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${dateFilter === f.v ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>{f.l}</button>
          ))}
          {/* Sem isso não dá pra saber se o número é atual ou congelou. */}
          <span className="ml-auto text-[11px] text-slate-400 tabular-nums">
            {reportUpdatedAt ? `Auto-updating · last ${reportUpdatedAt.toLocaleTimeString()}` : 'Auto-updating every 60s'}
          </span>
        </div>
      </Card>

      {/* Daily charts */}
      {loadingReport ? (
        <Card className="p-10 text-center text-slate-400 mb-5">Loading…</Card>
      ) : isBranding ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <LineCard title="Clicks per Day" data={mk((r) => n(r.clicks))} color="#ef4444" />
          <LineCard title="Success per Day" data={mk((r) => n(r.branding_success))} color="#22c55e" />
          <LineCard title="Engaged per Day" data={mk((r) => n(r.branding_engaged))} color="#3b82f6" />
          <LineCard title="Bounced per Day" data={mk((r) => n(r.branding_bounced))} color="#8b5cf6" />
          <LineCard title="Cost per Day" data={mk((r) => n(r.cost))} color="#f59e0b" format={(v) => `$${v.toFixed(0)}`} />
        </div>
      ) : isAdmin ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <LineCard title="Pageviews per Day" data={mk((r) => n(r.pageviews))} color="#3b82f6" />
          <LineCard title="Attempt per Day" data={mk((r) => n(r.attempt))} color="#ef4444" />
          <LineCard title="% Attempt per Day" data={mk((r) => (n(r.pageviews) ? (n(r.attempt) / n(r.pageviews)) * 100 : n(r.percent_attempt)))} color="#8b5cf6" format={(v) => `${v.toFixed(2)}%`} />
          <LineCard title="Clicks Delivered per Day" data={mk((r) => n(r.clicks))} color="#22c55e" />
          <LineCard title="Cost per Day" data={mk((r) => n(r.cost))} color="#f59e0b" format={(v) => `$${v.toFixed(0)}`} />
        </div>
      ) : (
        // Cliente: só cliques e pageviews.
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <LineCard title="Pageviews per Day" data={mk((r) => n(r.pageviews))} color="#3b82f6" />
          <LineCard title="Clicks Delivered per Day" data={mk((r) => n(r.clicks))} color="#22c55e" />
        </div>
      )}

      <DailyClicksHistory key={`${id}:${dateFilter}`} rows={loadingReport ? [] : histDesc}
        eventTypes={report?.event_types}
        isAdmin={isAdmin} isBranding={isBranding} loading={loadingReport}
        maxAttempts={campaign?.max_attempts_per_minute} maxAttemptPercent={campaign?.max_attempt_percent} />

      {/* Audience Insights (Athena/ALB logs) — admin e cliente */}
      {(() => {
        const d = insights?.data || null;
        const totals = d?.totals || {};
        const uniq = insights?.unique_users ?? totals.unique_users ?? null;
        const hits = insights?.hits ?? totals.hits ?? null;
        const perUser = uniq && hits ? (hits / uniq) : null;
        const byDay: Point[] = (d?.byDay || []).map((r) => ({ x: r.day.slice(5).replace('/', '/'), y: r.unique_users }));
        const devTotal = (d?.byDevice || []).reduce((a, r) => a + n(r.unique_users), 0);
        const ret = d?.retention && d.retention.users > 0 ? d.retention : null;
        // Agrupa "dias de visita por usuário" em faixas legíveis
        const dayBuckets = ret ? [
          { label: '1 day', users: ret.byDaysSeen.filter((b) => b.days_seen === 1).reduce((a, b) => a + b.users, 0) },
          { label: '2 days', users: ret.byDaysSeen.filter((b) => b.days_seen === 2).reduce((a, b) => a + b.users, 0) },
          { label: '3-5 days', users: ret.byDaysSeen.filter((b) => b.days_seen >= 3 && b.days_seen <= 5).reduce((a, b) => a + b.users, 0) },
          { label: '6-10 days', users: ret.byDaysSeen.filter((b) => b.days_seen >= 6 && b.days_seen <= 10).reduce((a, b) => a + b.users, 0) },
          { label: '11+ days', users: ret.byDaysSeen.filter((b) => b.days_seen >= 11).reduce((a, b) => a + b.users, 0) },
        ].filter((b) => b.users > 0) : [];
        const dayBucketMax = Math.max(1, ...dayBuckets.map((b) => b.users));
        const gapMax = ret ? Math.max(1, ...ret.gapBuckets.map((b) => b.users)) : 1;
        // Caches antigos gravaram os buckets em pt-BR — traduz na exibição
        const GAP_EN: Record<string, string> = { '~1 dia': '~1 day', '2-3 dias': '2-3 days', '4-7 dias': '4-7 days', '8-14 dias': '8-14 days', '15+ dias': '15+ days' };
        const monthOpts: { m: number; y: number }[] = [];
        for (let i = 0; i < 6; i++) {
          const dt = new Date(nowD.getFullYear(), nowD.getMonth() - i, 1);
          monthOpts.push({ m: dt.getMonth() + 1, y: dt.getFullYear() });
        }
        return (
          <Card className="overflow-hidden mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold text-ink-900">📈 Audience Insights</span>
              <div className="flex items-center gap-2">
                <select
                  value={`${insMonth}-${insYear}`}
                  onChange={(e) => { const [m, y] = e.target.value.split('-').map(Number); setInsMonth(m); setInsYear(y); }}
                  className="rounded-lg border border-border bg-surface px-2 py-1.5 text-sm outline-none"
                >
                  {monthOpts.map((o) => <option key={`${o.m}-${o.y}`} value={`${o.m}-${o.y}`}>{o.m}/{o.y}</option>)}
                </select>
                <Button variant="outline" disabled={insightsBusy} onClick={refreshInsights}>
                  {insightsBusy ? 'Running Athena… (~30s)' : '↻ Refresh'}
                </Button>
                {isAdmin && (
                  <Button className="bg-violet-600 hover:bg-violet-700 text-white" disabled={aiInsBusy || !d}
                    onClick={analyzeInsightsAI}
                    title={d ? `Analisa com IA os insights de ${insMonth}/${insYear}` : 'Rode o Refresh primeiro para gerar os dados do mês'}>
                    {aiInsBusy ? 'Analyzing… (~30s)' : '✨ Analyze with AI'}
                  </Button>
                )}
              </div>
            </div>
            <div className="p-4">
              {!insights && !insightsBusy && (
                <p className="text-sm text-slate-400">No insights computed for this month yet — click Refresh to run the analysis over the audience logs (unique users are also refreshed automatically every day at ~5am BRT).</p>
              )}
              {insights && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <Tile value={uniq != null ? uniq.toLocaleString() : '—'} label="Unique Users" sub="distinct signal IDs" tone="bg-brand-600" />
                    <Tile value={hits != null ? hits.toLocaleString() : '—'} label="Tag Events" sub="pixel hits (200)" tone="bg-sky-500" />
                    <Tile value={totals.unique_ips != null ? n(totals.unique_ips).toLocaleString() : '—'} label="Unique IPs" sub="distinct client IPs" tone="bg-emerald-600" />
                    <Tile value={perUser != null ? perUser.toFixed(1) : '—'} label="Events / User" sub="avg in the month" tone="bg-amber-500" />
                  </div>
                  {d ? (
                    <>
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                      <div className="xl:col-span-2">
                        <LineCard title="Unique Users per Day" data={byDay} color="#8b5cf6" />
                      </div>
                      <div className="space-y-4">
                        <div>
                          <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Devices (unique users)</div>
                          {(d.byDevice || []).map((r) => {
                            const pct = devTotal ? (n(r.unique_users) / devTotal) * 100 : 0;
                            return (
                              <div key={r.device} className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs text-slate-600 w-16">{r.device}</span>
                                <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${pct}%` }} /></div>
                                <span className="text-xs text-slate-500 w-24 text-right">{n(r.unique_users).toLocaleString()} · {pct.toFixed(0)}%</span>
                              </div>
                            );
                          })}
                          {(d.byDevice || []).length === 0 && <p className="text-xs text-slate-400">—</p>}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Events by Type</div>
                          <table className="w-full text-xs">
                            <tbody className="divide-y divide-border">
                              {(d.byEvent || []).map((r) => (
                                <tr key={r.event}>
                                  <td className="py-1 text-slate-600">{r.event}</td>
                                  <td className="py-1 text-right text-slate-700">{n(r.hits).toLocaleString()}</td>
                                  <td className="py-1 text-right text-slate-400">{n(r.unique_users).toLocaleString()} users</td>
                                </tr>
                              ))}
                              {(d.byEvent || []).length === 0 && <tr><td className="py-1 text-slate-400">—</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Retenção: quem retorna, quantas vezes vemos e em quanto tempo volta */}
                    {ret && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-3">Retention</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <Tile value={`${ret.returning_users.toLocaleString()}`} label="Returning Users"
                            sub={`${(ret.returning_pct ?? 0).toFixed(1)}% returned on another day`} tone="bg-teal-600" />
                          <Tile value={ret.avg_days_returning != null ? ret.avg_days_returning.toFixed(1) : '—'} label="Visit Days"
                            sub="avg among returning users" tone="bg-indigo-600" />
                          <Tile value={ret.avg_gap_days != null ? `~${ret.avg_gap_days.toFixed(1)} days` : '—'} label="Return Interval"
                            sub="avg time between visits" tone="bg-cyan-600" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Visit days per user</div>
                            {dayBuckets.map((b) => (
                              <div key={b.label} className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs text-slate-600 w-20">{b.label}</span>
                                <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-indigo-500" style={{ width: `${(b.users / dayBucketMax) * 100}%` }} /></div>
                                <span className="text-xs text-slate-500 w-28 text-right">{b.users.toLocaleString()} · {ret.users ? ((b.users / ret.users) * 100).toFixed(1) : 0}%</span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Days until return</div>
                            {ret.gapBuckets.map((b) => (
                              <div key={b.bucket} className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs text-slate-600 w-20">{GAP_EN[b.bucket] || b.bucket}</span>
                                <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-cyan-500" style={{ width: `${(b.users / gapMax) * 100}%` }} /></div>
                                <span className="text-xs text-slate-500 w-28 text-right">{b.users.toLocaleString()} · {ret.returning_users ? ((b.users / ret.returning_users) * 100).toFixed(1) : 0}%</span>
                              </div>
                            ))}
                            {ret.gapBuckets.length === 0 && <p className="text-xs text-slate-400">—</p>}
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-3">Users are identified by their signal ID (cookie/localStorage) — cleared cookies count as new users. Run ↻ Refresh to compute retention for months missing this data.</p>
                      </div>
                    )}

                    {d.context && <AudienceContext context={d.context} />}

                    {/* Event attribution: original request first, then correlated page context. */}
                    {((d.bySource && d.bySource.length > 0) || (d.byUtm && d.byUtm.length > 0) || (d.byReferrer && d.byReferrer.length > 0)) && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-3">Traffic Sources (UTM &amp; referrals)</div>
                        {d.bySource && d.bySource.length > 0 && (() => {
                          const totalSrc = d.bySource.reduce((a, r) => a + n(r.hits), 0) || 1;
                          return (
                            <div className="mb-4">
                              <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Event source mix</div>
                              {d.bySource.map((r) => {
                                const meta = SOURCE_META[r.category] || { label: r.category, color: '#64748b' };
                                const pct = (n(r.hits) / totalSrc) * 100;
                                return (
                                  <div key={r.category} className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs text-slate-600 w-28">{meta.label}</span>
                                    <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: meta.color }} /></div>
                                    <span className="text-xs text-slate-500 w-32 text-right">{n(r.hits).toLocaleString()} · {pct.toFixed(0)}% · {n(r.unique_users).toLocaleString()}u</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">By source / medium / campaign</div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-slate-400">
                                    <th className="py-1 font-medium">Source</th>
                                    <th className="py-1 font-medium">Medium</th>
                                    <th className="py-1 font-medium">Campaign</th>
                                    <th className="py-1 font-medium text-right">Hits</th>
                                    <th className="py-1 font-medium text-right">Users</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {(d.byUtm || []).map((r, i) => (
                                    <tr key={i}>
                                      <td className="py-1 pr-3 text-slate-700 break-all">{r.source || '—'}</td>
                                      <td className="py-1 pr-3 text-slate-600 break-all">{r.medium || '—'}</td>
                                      <td className="py-1 pr-3 text-slate-600 break-all">{r.campaign || '—'}</td>
                                      <td className="py-1 text-right text-slate-700">{n(r.hits).toLocaleString()}</td>
                                      <td className="py-1 text-right text-slate-400">{n(r.unique_users).toLocaleString()}</td>
                                    </tr>
                                  ))}
                                  {(d.byUtm || []).length === 0 && <tr><td colSpan={5} className="py-1 text-slate-400">No UTM-tagged traffic in this period.</td></tr>}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Top referrers</div>
                            {(() => {
                              const refs = d.byReferrer || [];
                              const max = Math.max(1, ...refs.map((r) => n(r.hits)));
                              return refs.length > 0 ? refs.map((r) => (
                                <div key={r.host} className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs text-slate-600 w-40 truncate" title={r.host}>{r.host}</span>
                                  <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-violet-500" style={{ width: `${(n(r.hits) / max) * 100}%` }} /></div>
                                  <span className="text-xs text-slate-500 w-24 text-right">{n(r.hits).toLocaleString()} · {n(r.unique_users).toLocaleString()}u</span>
                                </div>
                              )) : <p className="text-xs text-slate-400">No referrer captured in this period.</p>;
                            })()}
                          </div>
                        </div>
                        {d.byUtmRollup && (
                          <div className="mt-5">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="text-xs font-semibold text-slate-500 uppercase">Grouped total by</div>
                              <div className="flex gap-1">
                                {(['source', 'medium', 'campaign'] as const).map((g) => (
                                  <button key={g} onClick={() => setUtmGroupBy(g)}
                                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize ${utmGroupBy === g ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>
                                    {g}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-slate-400">
                                    <th className="py-1 font-medium capitalize">{utmGroupBy}</th>
                                    <th className="py-1 font-medium text-right">Hits</th>
                                    <th className="py-1 font-medium text-right">Users</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {(d.byUtmRollup[utmGroupBy] || []).map((r, i) => (
                                    <tr key={i}>
                                      <td className="py-1 pr-3 text-slate-700 break-all">{r.key || '—'}</td>
                                      <td className="py-1 text-right text-slate-700">{n(r.hits).toLocaleString()}</td>
                                      <td className="py-1 text-right text-slate-400">{n(r.unique_users).toLocaleString()}</td>
                                    </tr>
                                  ))}
                                  {(d.byUtmRollup[utmGroupBy] || []).length === 0 && <tr><td colSpan={3} className="py-1 text-slate-400">No data in this period.</td></tr>}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        <p className="text-[11px] text-slate-400 mt-3">Campaign parameters and the previous page reported by the browser describe each event's source. Original source signals take precedence; matched page context fills events with no source signals. The current page is not used as the previous page. Events without source information are not classified as direct.</p>
                      </div>
                    )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">Only unique users are cached for this month (daily job). Click Refresh for the full breakdown (per-day, events, devices).</p>
                  )}
                  {(d?.computed_at || insights.updated_at) && <p className="text-[11px] text-slate-400 mt-3">Last update: {new Date(d?.computed_at || insights.updated_at!).toLocaleString()}</p>}
                </>
              )}
              {insightsBusy && !insights && <p className="text-sm text-slate-400">Running Athena query over the month's logs…</p>}
            </div>
          </Card>
        );
      })()}

      {/* Cliente: o script de implementação faz parte do relatório dele. */}
      {!isAdmin && campaign && (
        <Card className="p-5 mb-6">
          <ImplementationCode poolId={campaign.campaign_id || null} jsId={campaign.campaign_js_id || null} brand={campaignBrand} />
        </Card>
      )}

      {isAdmin && (<>
      {/* Real-Time */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-ink-900">Real-Time Metrics (CloudWatch)</h2>
        <div className="flex items-center gap-1">
          {RT_PERIODS.map((p) => (
            <button key={p} onClick={() => setRtPeriod(p)} className={`px-2.5 py-1 rounded-md text-xs font-medium ${rtPeriod === p ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>{p}</button>
          ))}
          <Button className="ml-2 bg-violet-600 hover:bg-violet-700 text-white" disabled={aiBusy || !metrics}
            onClick={analyzeDelivery} title={`Analisa com IA os gráficos do período selecionado (${rtPeriod})`}>
            {aiBusy ? 'Analyzing… (~30s)' : '✨ Analyze with AI'}
          </Button>
        </div>
      </div>
      {metricsError ? (
        <Card className="p-5 mb-6 bg-rose-50 border-rose-200"><p className="text-sm text-rose-700">Metrics unavailable: {metricsError}</p></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Tile value={n(metrics?.clicks?.total).toLocaleString()} label="Clicks" sub="Last hour" tone="bg-emerald-600" />
            <Tile value={n(metrics?.attempts?.total).toLocaleString()} label="Attempts" sub="Last hour" tone="bg-sky-500" />
            <Tile value={n(metrics?.aborts?.total).toLocaleString()} label="Aborts" sub="Last hour" tone="bg-rose-500" />
          </div>
          {loadingMetrics ? (
            <Card className="p-8 text-center text-slate-400 mb-6">Loading metrics…</Card>
          ) : (
            <>
              {/* 3 colunas alinhadas com os tiles acima: Clicks | Attempts | Aborts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <LineCard title="Clicks Over Time" data={rtClicks} color="#22c55e" />
                <LineCard title="Attempts Over Time" data={rtAttempts} color="#3b82f6" />
                {abortSeries.series.length > 0 ? (
                  <MultiLineCard title="Abort Reasons Over Time" data={abortSeries.rows} series={abortSeries.series} height={180} />
                ) : (
                  <Card className="p-4 h-full">
                    <div className="text-sm font-medium text-slate-600 mb-3">Abort Reasons Over Time</div>
                    <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height: 180 }}>No aborts in this period.</div>
                  </Card>
                )}
              </div>
              <Card className="overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Abort Reasons Breakdown ({rtPeriod})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                        <th className="px-4 py-2.5 font-medium">Reason</th>
                        <th className="px-4 py-2.5 font-medium text-right">Count</th>
                        <th className="px-4 py-2.5 font-medium w-48">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {abortRows.map(([reason, count]) => {
                        const pct = abortTotal ? (count / abortTotal) * 100 : 0;
                        return (
                          <tr key={reason}>
                            <td className="px-4 py-2 text-rose-600 font-mono text-xs">{reason}</td>
                            <td className="px-4 py-2 text-right text-slate-700">{count.toLocaleString()}</td>
                            <td className="px-4 py-2"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-rose-500" style={{ width: `${pct}%` }} /></div><span className="text-xs text-slate-500 w-10 text-right">{pct.toFixed(1)}%</span></div></td>
                          </tr>
                        );
                      })}
                      {abortRows.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">No aborts in this period.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>

              <details className="mb-6">
                <summary className="cursor-pointer text-sm font-semibold text-ink-900 mb-2 select-none">ℹ Abort Reasons Legend</summary>
                <Card className="overflow-hidden mt-2">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted"><th className="px-4 py-2.5 font-medium">Reason</th><th className="px-4 py-2.5 font-medium">Description</th></tr></thead>
                    <tbody className="divide-y divide-border">
                      {ABORT_LEGEND.map((l) => (
                        <tr key={l.reason}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-600 whitespace-nowrap align-top">{l.reason}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">{l.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </details>
            </>
          )}
        </>
      )}

      {/* Fraud Guard */}
      <Card className="mb-6 border-brand-100">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b bg-brand-50 border-brand-100">
          <span className="text-sm font-semibold text-brand-700">🛡 Fraud Guard</span>
          <div className="flex gap-2">
            <Button className="bg-brand-600 hover:bg-brand-700" disabled={!!fraudBusy} onClick={() => runFraud('/fraud-guard/analyze', { campaignId: id, hoursBack: 24 }, 'analyze')}>{fraudBusy === 'analyze' ? '…' : '⚡ Analyze now'}</Button>
            <Button variant="outline" disabled={!!fraudBusy} onClick={() => { if (confirm('Force analysis via the configured AI provider? (consumes tokens)')) runFraud('/fraud-guard/analyze', { campaignId: id, hoursBack: 24, force: true }, 'llm'); }}>{fraudBusy === 'llm' ? '…' : '💡 Analyse with LLM'}</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" disabled={!!fraudBusy} onClick={() => { if (confirm('Recount and fix reports (48h)?')) runFraud('/fraud-guard/dedupe', { campaignId: id, hoursBack: 48 }, 'dedupe'); }}>{fraudBusy === 'dedupe' ? '…' : '↺ Deduplicate & fix'}</Button>
          </div>
        </div>
        <div className="p-4">
          {fraud?.latest ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Badge tone={fraud.latest.verdict === 'fraud' ? 'red' : fraud.latest.verdict === 'suspicious' ? 'amber' : 'green'}>{fraud.latest.verdict?.toUpperCase()}</Badge>
                <span className="text-xs text-slate-400">{fraud.latest.mode} · {fraud.latest.window} · {new Date(fraud.latest.created_at).toLocaleString()}{fraud.latest.model ? ` · ${fraud.latest.model} · ${fraud.latest.tokens ?? 0} tok` : ' · 0 tok'}</span>
              </div>
              <p className="text-sm text-slate-600">{fraud.latest.summary}</p>
              {(fraud.latest.signals?.length ?? 0) > 0 && (
                <ul className="mt-2 text-xs text-slate-500 list-disc pl-5 space-y-0.5">{fraud.latest.signals!.map((s, i) => <li key={i}>{s}</li>)}</ul>
              )}
            </>
          ) : <p className="text-sm text-slate-400">No analysis yet. Click "Analyze now".</p>}

          {(fraud?.rules?.length ?? 0) > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-600 mb-1">Active rules ({fraud!.rules.filter((r) => (r as any).status !== 'off').length})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-500"><th className="px-2 py-1.5 font-medium">Type</th><th className="px-2 py-1.5 font-medium">Value</th><th className="px-2 py-1.5 font-medium">Scope</th><th className="px-2 py-1.5 font-medium text-right">Blocked</th><th className="px-2 py-1.5 font-medium">Last hit</th><th className="px-2 py-1.5 font-medium">Filtering?</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {fraud!.rules.map((r, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1.5"><code>{r.type}</code></td>
                        <td className="px-2 py-1.5"><code>{r.value}</code></td>
                        <td className="px-2 py-1.5">{r.campaign_id === '*' ? 'global' : 'campaign'}</td>
                        <td className="px-2 py-1.5 text-right font-medium">{n(r.hits).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-slate-400">{r.last_hit_at ? new Date(r.last_hit_at).toLocaleString() : '—'}</td>
                        <td className="px-2 py-1.5">{r.still_filtering ? <Badge tone="green">yes</Badge> : <Badge tone="neutral">idle</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Card>
      </>)}

      {/* Tag Events (admin e cliente) */}
      {(() => {
        const q = tagFilter.trim().toLowerCase();
        const filtered = q ? tagEvents.filter((e) => JSON.stringify(e).toLowerCase().includes(q)) : tagEvents;
        return (
          <Card className="overflow-hidden mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold text-ink-900">📡 Tag Events (Last {tagEvents.length})</span>
              <div className="flex gap-2">
                <Button variant="outline" disabled={tagBusy} onClick={loadTagEvents}>{tagBusy ? '…' : '↻ Check Last 100 Events'}</Button>
                <Button className="bg-sky-600 hover:bg-sky-700" disabled={prodCheckBusy} onClick={checkProducts}>{prodCheckBusy ? '…' : '⚖ Check Product IDs with Destination'}</Button>
                {isAdmin && <Button variant="danger" disabled={tagBusy} onClick={clearAndRecollect}>🗑 Clear &amp; Recollect</Button>}
              </div>
            </div>
            <div className="px-4 py-2 border-b border-border">
              <input value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="Filter events… (searches any field)"
                className="w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface-muted"><tr className="text-left text-slate-500"><th className="px-3 py-2 font-medium">Timestamp</th><th className="px-3 py-2 font-medium">Event</th><th className="px-3 py-2 font-medium">Product ID</th><th className="px-3 py-2 font-medium">Referrer</th><th className="px-3 py-2 font-medium">User ID</th><th className="px-3 py-2 font-medium">Device</th><th className="px-3 py-2 font-medium">IP</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {filtered.slice(0, 100).map((e, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtTs(e.timestamp)}</td>
                      <td className="px-3 py-1.5">{String(e.eventType || '—')}</td>
                      {/* itemId é o productId que a tag envia no setDmpData */}
                      <td className="px-3 py-1.5 font-mono text-slate-600 max-w-[160px] truncate" title={String(e.itemId || e.productId || '')}>{String(e.itemId || e.productId || '—')}</td>
                      <td className="px-3 py-1.5 text-slate-500 max-w-[200px] truncate">{String(e.referrer || e.referer || '—')}</td>
                      <td className="px-3 py-1.5 font-mono text-slate-400">{String(e.userId || '').slice(0, 18)}</td>
                      <td className="px-3 py-1.5">{String(e.device || '—')}</td>
                      <td className="px-3 py-1.5 text-slate-500">{String(e.ip || '—')}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">{q ? 'No events match the filter.' : 'No events.'}</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* Insights gerados por IA (delivery metrics ou audience insights) */}
      <AiInsightsModal open={aiOpen} onClose={() => setAiOpen(false)} title={aiTitle} result={aiResult} />

      {/* Relatório do match productId (tag events) × destination set */}
      <Modal open={prodCheckOpen} onClose={() => setProdCheckOpen(false)} title="Product IDs × Destination"
        footer={<Button variant="outline" onClick={() => setProdCheckOpen(false)}>Close</Button>}>
        {!prodCheck ? null : !prodCheck.hasDestination ? (
          <p className="text-sm text-slate-600">This campaign has no Destination Set linked — link one in the campaign form to run this check.</p>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Destination: <span className="font-semibold text-ink-900">{prodCheck.destination?.name || prodCheck.destination?.id}</span>
              <br />Scanned <strong>{prodCheck.eventsScanned}</strong> events ({prodCheck.eventsWithProduct} with product) · <strong>{prodCheck.uniqueIds}</strong> unique product IDs
            </div>
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-emerald-700">{prodCheck.matched?.length || 0}</div>
                <div className="text-xs text-emerald-700">found in destination</div>
              </div>
              <div className="flex-1 rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-center">
                <div className="text-2xl font-bold text-rose-700">{prodCheck.unmatched?.length || 0}</div>
                <div className="text-xs text-rose-700">not found</div>
              </div>
            </div>
            {(prodCheck.uniqueIds || 0) === 0 ? (
              <p className="text-sm text-slate-400">No product IDs in the last events — only non-product events (home, search…) were collected.</p>
            ) : (
              <div className="max-h-72 overflow-auto rounded-lg border border-border divide-y divide-border text-xs font-mono">
                {(prodCheck.unmatched || []).map((pid) => (
                  <div key={`u-${pid}`} className="px-3 py-1.5 flex gap-2 items-baseline">
                    <span className="text-rose-600 font-bold shrink-0">✗</span>
                    <span className="text-ink-900">{pid}</span>
                    <span className="text-slate-400 font-sans">not found in destination</span>
                  </div>
                ))}
                {(prodCheck.matched || []).map((m) => (
                  <div key={`m-${m.id}`} className="px-3 py-1.5 flex gap-2 items-baseline">
                    <span className="text-emerald-600 font-bold shrink-0">✓</span>
                    <span className="text-ink-900">{m.id}</span>
                    {m.name && <span className="text-slate-500 font-sans truncate">{m.name}</span>}
                    {m.inStock === false && <span className="text-amber-600 font-sans shrink-0">(out of stock)</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {isAdmin && (<>
      {/* Debug GetJS */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-ink-900">
            Debug GetJS (Last 100 Attempts){debugLoaded ? ` — ${debugJs.length} found` : ''}
          </span>
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={loadDebugJs} disabled={debugBusy}>
            {debugBusy ? '⏳ Loading…' : '↻ Load'}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-muted"><tr className="text-left text-slate-500"><th className="px-3 py-2 font-medium">Timestamp</th><th className="px-3 py-2 font-medium">Method</th><th className="px-3 py-2 font-medium">User ID</th><th className="px-3 py-2 font-medium">getJS <span className="text-slate-400">(click)</span></th><th className="px-3 py-2 font-medium">Obfuscated <span className="text-slate-400">(click)</span></th></tr></thead>
            <tbody className="divide-y divide-border align-top">
              {debugJs.slice(0, 100).map((e, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtTs(e.timestamp)}</td>
                  <td className="px-3 py-2"><Badge tone="blue">{String(e.methodUsed || e.method || 'unknown')}</Badge></td>
                  <td className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">{String(e.userId || '').slice(0, 18)}</td>
                  <td className="px-3 py-2 min-w-[240px]"><ExpandableCode text={String(e.getJS || '')} /></td>
                  <td className="px-3 py-2 min-w-[240px]"><ExpandableCode text={String(e.obfuscatedJS || '')} /></td>
                </tr>
              ))}
              {debugJs.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                  {debugLoaded ? 'No getJS debug data.' : 'Click "Load" to fetch the last 100 getJS attempts for this campaign.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Campaign Change Log */}
      <Card className="overflow-hidden mt-6">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">
          Campaign Change Log (Last 100 Changes)
        </div>
        {logsError && <div className="px-4 py-3 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">{logsError}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                <th className="px-4 py-2.5 font-medium whitespace-nowrap">Date/Time</th>
                <th className="px-4 py-2.5 font-medium">Action</th>
                <th className="px-4 py-2.5 font-medium">User</th>
                <th className="px-4 py-2.5 font-medium">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border align-top">
              {logs.map((log) => {
                const raw = log.changes;
                let parsed: Record<string, ChangeEntry> = {};
                try {
                  parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
                } catch {
                  parsed = {};
                }
                const action = String(log.action || '').toLowerCase();
                return (
                  <tr key={log.id}>
                    {/* created_at é TIMESTAMP: instante real, hora local está certo aqui. */}
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtTs(log.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={LOG_ACTION_TONE[action] || 'neutral'}>
                        {action === 'create' ? 'Created' : action === 'delete' ? 'Deleted'
                          : action === 'duplicate' ? 'Duplicated' : 'Updated'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{log.user_email || '—'}</td>
                    <td className="px-4 py-2.5">
                      {action === 'create' ? (
                        <span className="text-emerald-600">Campaign created</span>
                      ) : action === 'delete' ? (
                        <span className="text-rose-600">Campaign deleted</span>
                      ) : action === 'duplicate' ? (
                        <span className="text-amber-600">
                          Duplicated from: {String(
                            (parsed as unknown as Record<string, unknown>).original_campaign_name ??
                            (parsed as unknown as Record<string, unknown>).original_campaign_id ?? '—'
                          )}
                        </span>
                      ) : Object.keys(parsed).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="space-y-1">
                          {Object.entries(parsed).map(([field, change]) => (
                            <div key={field} className="text-xs">
                              <span className="font-semibold text-ink-900">{logFieldLabel(field)}:</span>{' '}
                              <span className="text-rose-600 line-through">{logValue(change?.old)}</span>
                              <span className="text-slate-400 mx-1">›</span>
                              <span className="text-emerald-600">{logValue(change?.new)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && !logsError && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No changes recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>)}
    </Layout>
  );
}
